import { ChakraProvider } from '@chakra-ui/react';
import '@fontsource/source-sans-pro/300.css';
import '@fontsource/source-sans-pro/400.css';
import '@fontsource/source-sans-pro/600.css';
import '@fontsource/source-sans-pro/700.css';

import { createRef, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import {
  initAuth,
  selectImpersonatedUser,
  selectImpersonationOverrides,
  selectIsImpersonating,
  useAuthStore,
  useImpersonationSelector,
} from '@revelio/auth';
import { LoadingGifProvider, ServiceWorker, userTracking } from '@revelio/core';
import {
  resetPlotColors as resetReplotsColors,
  resetOtherColor as resetReplotsOtherColor,
  setPlotColors as setReplotsColors,
  setOtherColor as setReplotsOtherColor,
} from '@revelio/replots';
import { UITheme } from '@revelio/ui';

import './brands';

import { environment } from './environments/environment';
import { OpenReplayProvider } from './hooks/open-replay';
import { Next } from './next/next';
import {
  BrandProvider,
  useBrand,
  useBrandCssVars,
} from './shared/branding/brand-provider';
import { createBrandedTheme } from './shared/branding/create-branded-theme';
import { MaintenanceToast } from './shared/components';
import { TanstackQueryProvider } from './shared/data-fetch/tanstack-query-provider';
import { RouteAwareUrqlProvider } from './shared/data-fetch/urql-provider';
import './shared/firebase/firebase-performance';

import { KibanaLoggerProvider } from './shared/hooks/kibana-logger/kibana-logger';
import { kibanaLogger } from './shared/logging/kibana-logger';
import { UnleashFlags } from './shared/unleash-flags/unleash-flags';

const isWilsonHost =
  typeof window !== 'undefined' &&
  window.location.hostname.toLowerCase().startsWith('wilson.');

initAuth({
  oryKratosRoot: environment.ORY_KRATOS_URL,
  dashMetaRoot: environment.DASH_META_API,
  accountPortalRoot: isWilsonHost
    ? environment.WILSON_ACCOUNT_PORTAL_ROOT
    : environment.ACCOUNT_PORTAL_ROOT,
  accountPortalBackendRoot: environment.ACCOUNT_PORTAL_BACKEND_ROOT,
});

if (document.cookie.includes('mswMockDashboard')) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { worker } = require('./mocks/browser');
  worker.start({ onUnhandledRequest: 'bypass' });
}

/** Listen for uncaught promise rejections.
 * This is being setup to specifically handle uncaught rejections from an OpenReplay custom fetch function
 * We are catching the specific error and stopping its propagation */
window.addEventListener('unhandledrejection', (event) => {
  event.promise.catch((err) => {
    // ignore user aborted request thrown by OpenReplay
    if (
      err?.name === 'AbortError' &&
      err?.message === 'The user aborted a request.'
    ) {
      event.preventDefault();
      return;
    }
  });
});

userTracking.init(environment.mixpanelToken, {
  debug: false,
  opt_out_tracking_by_default: !environment.production,
});

const toastRef = createRef<HTMLDivElement>();
const toastOptions = {
  portalProps: {
    containerRef: toastRef,
  },
};

const uiThemePortalId = 'ui-portal-root';

function BrandedApp({ children }: { children: React.ReactNode }) {
  const realClientName = useAuthStore((s) => s.user?.client_name);
  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const impersonatedUser = useImpersonationSelector(selectImpersonatedUser);
  const overrides = useImpersonationSelector(selectImpersonationOverrides);

  const clientName = useMemo(() => {
    // Wilson hostname always renders Wilson branding, regardless of who's
    // logged in. Lets pre-auth visitors and any cross-brand users on
    // wilson.* see the white-label experience.
    if (isWilsonHost) {
      return 'Wilson HR';
    }
    if (isImpersonating && impersonatedUser?.client_name !== undefined) {
      return impersonatedUser.client_name;
    }
    if (overrides?.clientName !== undefined) {
      return overrides.clientName;
    }
    return realClientName;
  }, [realClientName, isImpersonating, impersonatedUser, overrides]);

  return (
    <BrandProvider clientName={clientName}>
      <BrandedChakraProvider>{children}</BrandedChakraProvider>
    </BrandProvider>
  );
}

function BrandedChakraProvider({ children }: { children: React.ReactNode }) {
  const brand = useBrand();
  useBrandCssVars();
  const brandedTheme = useMemo(() => createBrandedTheme(brand), [brand]);

  // Sync chart colors synchronously (before children render) so charts
  // see the correct palette on their very first render.
  useMemo(() => {
    if (brand.chartColors) {
      setReplotsColors(brand.chartColors);
    } else {
      resetReplotsColors();
    }
  }, [brand.chartColors]);

  useMemo(() => {
    if (brand.otherColor) {
      setReplotsOtherColor(brand.otherColor);
    } else {
      resetReplotsOtherColor();
    }
  }, [brand.otherColor]);

  return (
    <ChakraProvider
      theme={brandedTheme}
      portalZIndex={40}
      toastOptions={toastOptions}
    >
      <LoadingGifProvider gif={brand.loadingGif}>{children}</LoadingGifProvider>
    </ChakraProvider>
  );
}

const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <RouteAwareUrqlProvider>
        <OpenReplayProvider>
          <ServiceWorker />
          <TanstackQueryProvider>
            <UnleashFlags>
              <UITheme portalId={uiThemePortalId}>
                <BrandedApp>
                  <Next />
                  <div ref={toastRef} />
                  <MaintenanceToast />
                </BrandedApp>
                <div id={uiThemePortalId} />
              </UITheme>
            </UnleashFlags>
          </TanstackQueryProvider>
        </OpenReplayProvider>
      </RouteAwareUrqlProvider>
    ),
  },
]);

const rootContainer = document.getElementById('next') as Element;
const root = createRoot(rootContainer);

root.render(
  <KibanaLoggerProvider kibanaLogger={kibanaLogger}>
    <RouterProvider router={router} />
  </KibanaLoggerProvider>
);
